import React, { useState, useEffect } from 'react';
import { X, User, Save, XCircle, AlertCircle, Edit3, Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import { User as UserType } from '../types/User';
import { PerfilAcesso } from '../types/Permission';

interface UsuarioModalProps {
  usuario: UserType | null;
  onClose: () => void;
  onSave: (usuarioData: Partial<UserType> & { password?: string }) => Promise<void>;
}

// Funções de máscara
const maskCPF = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    .substring(0, 14);
};

const maskPhone = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{4,5})(\d{4})$/, '$1-$2')
    .substring(0, 15);
};

const UsuarioModal: React.FC<UsuarioModalProps> = ({ usuario, onClose, onSave }) => {
  const { userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(!usuario);
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [perfisAcesso, setPerfisAcesso] = useState<PerfilAcesso[]>([]);
  const [cargos, setCargos] = useState<Array<{ id: string; nome: string }>>([]);
  const [loadingPerfis, setLoadingPerfis] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!userProfile?.empresaCnpj) return;

      setLoadingPerfis(true);
      try {
        const [perfisData, cargosData] = await Promise.all([
          firebaseService.getPerfisAcesso(userProfile.empresaCnpj),
          firebaseService.getCargos()
        ]);
        setPerfisAcesso(perfisData.filter(p => p.ativo));
        setCargos(cargosData);
      } catch (error) {
        console.error('Erro ao carregar perfis/cargos:', error);
      } finally {
        setLoadingPerfis(false);
      }
    };

    loadData();
  }, [userProfile?.empresaCnpj]);

  useEffect(() => {
    if (usuario) {
      setFormData({
        nome: usuario.nome || '',
        email: usuario.email || '',
        telefone: usuario.telefone || '',
        cellular: usuario.cellular || '',
        cpf: usuario.cpf || '',
        cargo: usuario.cargo || 'administrador',
        perfilAcessoId: usuario.perfilAcessoId || '',
        bAtivo: usuario.bAtivo !== undefined ? usuario.bAtivo : true,
        password: ''
      });
      setIsEditing(false);
    } else {
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        cellular: '',
        cpf: '',
        cargo: 'administrador',
        perfilAcessoId: '',
        bAtivo: true,
        password: ''
      });
      setIsEditing(true);
    }
    setErrors({});
  }, [usuario]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCPF = (cpf: string): boolean => {
    if (!cpf) return true;
    const cleanCPF = cpf.replace(/\D/g, '');
    return cleanCPF.length === 11;
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true;
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10;
  };

  const validatePassword = (password: string): boolean => {
    if (!usuario && !password) return false; // Senha obrigatória ao criar
    if (password && password.length < 6) return false; // Mínimo 6 caracteres
    return true;
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.telefone && !formData.cellular) {
      newErrors.telefone = 'Telefone ou celular é obrigatório';
    }

    if (formData.telefone && !validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido';
    }

    if (formData.cellular && !validatePhone(formData.cellular)) {
      newErrors.cellular = 'Celular inválido';
    }

    if (formData.cpf && !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    if (!formData.cargo) {
      newErrors.cargo = 'Cargo é obrigatório';
    }

    if (!usuario && !validatePassword(formData.password)) {
      newErrors.password = 'Senha é obrigatória e deve ter no mínimo 6 caracteres';
    } else if (formData.password && !validatePassword(formData.password)) {
      newErrors.password = 'Senha deve ter no mínimo 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    let processedValue = value;

    if (field === 'cpf') {
      processedValue = maskCPF(value);
    } else if (field === 'telefone' || field === 'cellular') {
      processedValue = maskPhone(value);
    }

    setFormData((prev: any) => ({
      ...prev,
      [field]: processedValue
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      const usuarioData: Partial<UserType> & { password?: string } = {
        ...(usuario || {}),
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        cellular: formData.cellular,
        cpf: formData.cpf,
        cargo: formData.cargo,
        perfilAcessoId: formData.perfilAcessoId || undefined,
        bAtivo: formData.bAtivo
      };

      if (formData.password) {
        usuarioData.password = formData.password;
      }

      await onSave(usuarioData);
      setIsEditing(false);
      if (!usuario) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      setErrors({ submit: 'Erro ao salvar usuário. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (usuario) {
      setFormData({
        nome: usuario.nome || '',
        email: usuario.email || '',
        telefone: usuario.telefone || '',
        cellular: usuario.cellular || '',
        cpf: usuario.cpf || '',
        cargo: usuario.cargo || 'administrador',
        perfilAcessoId: usuario.perfilAcessoId || '',
        bAtivo: usuario.bAtivo !== undefined ? usuario.bAtivo : true,
        password: ''
      });
      setIsEditing(false);
    } else {
      onClose();
    }
    setErrors({});
  };

  const isCreating = !usuario;
  const displayName = usuario?.nome || 'Novo Usuário';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').substring(0, 2);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                {usuario?.fotoUrl ? (
                  <img 
                    src={usuario.fotoUrl} 
                    alt={displayName}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-medium text-primary-600">
                    {initials}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {isCreating ? 'Novo Usuário' : (isEditing ? 'Editar Usuário' : 'Detalhes do Usuário')}
                </h2>
                {usuario && <p className="text-sm text-gray-500 dark:text-gray-400">ID: {usuario.id}</p>}
              </div>
            </div>
            <div className="flex items-center gap-x-2">
              {!isEditing && !isCreating ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Editar
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={saving}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <User className="h-5 w-5" />
                Informações Pessoais
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome Completo <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.nome ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Nome completo do usuário"
                    />
                    {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{usuario?.nome || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="email@exemplo.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{usuario?.email || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefone
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="tel"
                      value={formData.telefone}
                      onChange={(e) => handleInputChange('telefone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.telefone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="(00) 0000-0000"
                    />
                    {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{usuario?.telefone || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Celular
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="tel"
                      value={formData.cellular}
                      onChange={(e) => handleInputChange('cellular', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.cellular ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="(00) 00000-0000"
                    />
                    {errors.cellular && <p className="text-red-500 text-xs mt-1">{errors.cellular}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{usuario?.cellular || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CPF
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.cpf}
                      onChange={(e) => handleInputChange('cpf', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.cpf ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="000.000.000-00"
                    />
                    {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{usuario?.cpf || 'Não informado'}</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <Shield className="h-5 w-5" />
                Acesso e Permissões
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cargo <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <select
                      value={formData.cargo}
                      onChange={(e) => handleInputChange('cargo', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.cargo ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      disabled={loadingPerfis}
                    >
                      {loadingPerfis ? (
                        <option>Carregando...</option>
                      ) : cargos.length === 0 ? (
                        <option value="">Nenhum cargo disponível</option>
                      ) : (
                        <>
                          <option value="">Selecione um cargo</option>
                          {cargos.map(cargo => (
                            <option key={cargo.id} value={cargo.id}>{cargo.nome}</option>
                          ))}
                        </>
                      )}
                    </select>
                    {errors.cargo && <p className="text-red-500 text-xs mt-1">{errors.cargo}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{usuario?.cargo || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Perfil de Acesso
                </label>
                {isEditing || isCreating ? (
                  <>
                    <select
                      value={formData.perfilAcessoId}
                      onChange={(e) => handleInputChange('perfilAcessoId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={loadingPerfis}
                    >
                      <option value="">Nenhum (usar permissões do cargo)</option>
                      {perfisAcesso.map(perfil => (
                        <option key={perfil.id} value={perfil.id}>{perfil.nome}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Selecione um perfil de acesso para personalizar permissões
                    </p>
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">
                    {usuario?.perfilAcessoId 
                      ? perfisAcesso.find(p => p.id === usuario.perfilAcessoId)?.nome || 'Não encontrado'
                      : 'Nenhum'}
                  </p>
                )}
              </div>

              {(isEditing || isCreating) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {isCreating ? 'Senha' : 'Nova Senha (deixe em branco para manter)'} <span className={isCreating ? 'text-red-500' : ''}>{isCreating ? '*' : ''}</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`w-full px-3 py-2 pr-10 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder={isCreating ? 'Mínimo 6 caracteres' : 'Deixe em branco para manter'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                  {!isCreating && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Deixe em branco para manter a senha atual
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.bAtivo}
                    onChange={(e) => handleInputChange('bAtivo', e.target.checked)}
                    disabled={!isEditing && !isCreating}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Usuário Ativo</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Usuários inativos não podem fazer login no sistema
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsuarioModal;

